import React from 'react';
import { TrendingUp, Sprout } from 'lucide-react';
import PageHero from '../components/PageHero';
import CTASection from '../components/CTASection';
import { TIMELINE, IMAGES } from '../data/mock';
import useReveal from '../hooks/useReveal';

export default function OurGrowth() {
  useReveal([]);
  return (
    <div>
      <PageHero
        eyebrow="Our growth"
        lead="Inviting growth, organically"
        title="Our Growth"
        subtitle="A founder-led platform for robust investment in agribusiness projects, fuelled by organic demand and the thrust of technology."
        image={IMAGES.rollingHills}
      />

      {/* INTRO */}
      <section className="py-24">
        <div className="container-fw grid items-center gap-14 lg:grid-cols-2">
          <div className="reveal">
            <span className="eyebrow">2021 to 2023</span>
            <h2 className="mt-4 display-xl text-4xl text-farmway-cream sm:text-5xl">From $30M to $100M+</h2>
            <p className="mt-5 text-farmway-cream/75">
              In just two years, Farmway, a founder-led platform for robust investment portfolios in
              food security and agriculture, grew to a <span className="text-farmway-cream">$30 million</span> asset
              base with support from a reputable network. Fuelled by organic demand, we’re poised to
              surpass <span className="text-farmway-gold">$100 million</span>.
            </p>
          </div>
          <div className="reveal grid grid-cols-2 gap-4" data-delay="120">
            <div className="rounded-2xl border border-white/10 bg-farmway-green-mid/40 p-6">
              <TrendingUp className="h-7 w-7 text-farmway-gold" />
              <p className="mt-4 font-display text-3xl font-black text-farmway-cream">$30M</p>
              <p className="mt-1 text-xs text-farmway-cream/60">Asset base, 2023</p>
            </div>
            <div className="mt-8 rounded-2xl border border-white/10 bg-farmway-gold/10 p-6">
              <Sprout className="h-7 w-7 text-farmway-gold" />
              <p className="mt-4 font-display text-3xl font-black text-farmway-gold">$100M+</p>
              <p className="mt-1 text-xs text-farmway-cream/60">Targeted, 2024</p>
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="relative overflow-hidden bg-farmway-green-dark py-24">
        <div className="container-fw">
          <div className="mb-16 max-w-2xl reveal">
            <span className="eyebrow">Milestones</span>
            <h2 className="mt-4 display-xl text-4xl text-farmway-cream sm:text-5xl">A story of measured growth</h2>
          </div>

          <div className="relative">
            <div className="absolute left-4 top-0 h-full w-px bg-white/10 md:left-1/2" />
            <div className="space-y-12">
              {TIMELINE.map((t, i) => (
                <div key={t.year} className={`reveal relative flex flex-col gap-6 md:flex-row md:items-center ${i % 2 === 1 ? 'md:flex-row-reverse' : ''}`} data-delay={i * 80}>
                  <div className="md:w-1/2">
                    <div className={`rounded-2xl border border-white/10 bg-farmway-green-mid/40 p-7 ${i % 2 === 1 ? 'md:ml-10' : 'md:mr-10'}`}>
                      <p className="font-display text-2xl font-black text-farmway-gold">{t.year}</p>
                      <ul className="mt-4 space-y-2.5">
                        {t.items.map((item) => (
                          <li key={item} className="flex items-start gap-2.5 text-sm text-farmway-cream/80">
                            <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-farmway-leaf" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="absolute left-4 h-4 w-4 -translate-x-1/2 rounded-full border-4 border-farmway-green-dark bg-farmway-gold md:left-1/2" />
                  <div className="hidden md:block md:w-1/2" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
